import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Plus, 
  Search,
  TrendingUp,
  User,
  Calendar,
  Target,
  ArrowRight,
  ArrowLeft,
  XCircle,
  Edit,
  Edit3,
  Info,
  FileDown,
  Loader2,
} from 'lucide-react';

const PHASE_CONFIG = {
  PHASE_1: { label: 'Phase 1 - Initial', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  PHASE_2: { label: 'Phase 2 - Progression', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  PHASE_3: { label: 'Phase 3 - Advanced', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  PHASE_4: { label: 'Phase 4 - Return', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  CLEARED: { label: 'Cleared', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' }
};

const STATUS_CONFIG = {
  ACTIVE: { label: 'Active', color: 'bg-amber-500/20 text-amber-400' },
  CLEARED: { label: 'Cleared', color: 'bg-green-500/20 text-green-400' },
  CANCELLED: { label: 'Cancelled', color: 'bg-zinc-500/20 text-zinc-400' }
};

// Canonical injury taxonomy (must match backend INJURY_TYPES).
const INJURY_TYPES = [
  'Hamstring', 'Quadriceps', 'Adductor / Groin', 'Calf',
  'Knee Ligament', 'Meniscus', 'Ankle Sprain', 'Shoulder',
  'Back / Lumbar', 'Muscle Fatigue / Overload', 'Concussion',
  'Tendinopathy', 'Fracture', 'Illness', 'Other',
];

export default function StaffRTPPage() {
  const { api, user, hasRole } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [profiles, setProfiles] = useState([]);
  const [caps, setCaps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [weeklyLogs, setWeeklyLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [teams, setTeams] = useState([]);
  const [athletes, setAthletes] = useState([]);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [phaseChangeTarget, setPhaseChangeTarget] = useState(null); // for confirm dialog
  const [phaseCaps, setPhaseCaps] = useState([]);
  const [editingCap, setEditingCap] = useState(null);
  const [editCapValue, setEditCapValue] = useState('');
  const [editCapIncrease, setEditCapIncrease] = useState('');
  
  // Session history & editable notes
  const [profileSessions, setProfileSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [detailTab, setDetailTab] = useState('overview');
  const [exportingRtp, setExportingRtp] = useState(false);
  
  // New profile form
  const [newProfile, setNewProfile] = useState({
    athlete_id: '',
    team_id: '',
    injury_type: '',
    injury_date: new Date().toISOString().split('T')[0],
    injury_location: '',
    injury_description: '',
    expected_duration_weeks: '',
    notes: ''
  });

  const canWrite = ['ADMIN', 'SNC', 'PHYSIO'].includes(user?.role);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [profilesRes, capsRes, summaryRes, logsRes, teamsRes, athletesRes, phCapsRes] = await Promise.all([
        api.get(`/rtp/profiles?status=${statusFilter}`),
        api.get('/rtp/caps?active_only=true'),
        api.get('/rtp/summary'),
        api.get('/rtp/weekly-logs'),
        api.get('/teams'),
        api.get('/athletes'),
        api.get('/rtp/phase-caps').catch(() => ({ data: [] })),
      ]);
      
      setProfiles(profilesRes.data || []);
      setCaps(capsRes.data || []);
      setSummary(summaryRes.data);
      setWeeklyLogs(logsRes.data || []);
      setTeams(teamsRes.data || []);
      setAthletes(athletesRes.data || []);
      setPhaseCaps(phCapsRes.data || []);
    } catch (error) {
      console.error('Error fetching RTP data:', error);
      toast.error('Failed to load RTP data');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateProfile = async () => {
    try {
      await api.post('/rtp/profiles', {
        ...newProfile,
        expected_duration_weeks: newProfile.expected_duration_weeks ? parseInt(newProfile.expected_duration_weeks) : null
      });
      
      toast.success('RTP profile created successfully');
      setCreateDialogOpen(false);
      setNewProfile({
        athlete_id: '',
        team_id: '',
        injury_type: '',
        injury_date: new Date().toISOString().split('T')[0],
        injury_location: '',
        injury_description: '',
        expected_duration_weeks: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    }
  };

  const handleChangePhase = async (profileId, newPhase) => {
    try {
      await api.post(`/rtp/profiles/${profileId}/advance-phase?new_phase=${newPhase}`);
      toast.success(`Phase changed to ${PHASE_CONFIG[newPhase]?.label || newPhase}`);
      fetchData();
      setDetailDialogOpen(false);
      setPhaseChangeTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change phase');
    }
  };

  const handleSaveCap = async (capId) => {
    try {
      const updates = {};
      if (editCapValue !== '') updates.cap_value = parseFloat(editCapValue);
      if (editCapIncrease !== '') updates.max_increase_pct = parseFloat(editCapIncrease);
      await api.put(`/rtp/phase-caps/${capId}`, updates);
      toast.success('Cap updated');
      setEditingCap(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update cap');
    }
  };

  const handleClearProfile = async (profileId) => {
    try {
      await api.post(`/rtp/profiles/${profileId}/clear`);
      
      toast.success('Athlete cleared for return to play');
      fetchData();
      setDetailDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear profile');
    }
  };

  const handleExportRtpReport = async (profile) => {
    if (!profile?.athlete_id) {
      toast.error('Missing athlete reference for this RTP case');
      return;
    }
    setExportingRtp(true);
    try {
      const res = await api.post(
        '/premium-reports/return-to-play',
        { athlete_id: profile.athlete_id, rtp_profile_id: profile.id },
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (profile.athlete_name || 'athlete').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
      a.download = `rtp_report_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('RTP Report downloaded');
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Failed to export RTP Report';
      toast.error(detail);
    } finally {
      setExportingRtp(false);
    }
  };

  const fetchProfileSessions = async (profileId) => {
    try {
      setSessionsLoading(true);
      const res = await api.get(`/rtp/profiles/${profileId}/sessions`);
      setProfileSessions(res.data || []);
    } catch (error) {
      console.error('Failed to fetch RTP sessions', error);
      setProfileSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedProfile) return;
    try {
      await api.put(`/rtp/profiles/${selectedProfile.id}`, { notes: notesValue });
      toast.success('Notes updated');
      setEditingNotes(false);
      setSelectedProfile({ ...selectedProfile, notes: notesValue });
      fetchData();
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const openProfileDetail = (profile) => {
    setSelectedProfile(profile);
    setNotesValue(profile.notes || '');
    setEditingNotes(false);
    setDetailTab('overview');
    setDetailDialogOpen(true);
    fetchProfileSessions(profile.id);
  };

  const filteredProfiles = profiles.filter(p => 
    (p.athlete_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     p.injury_type?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getNextPhase = (currentPhase) => {
    const phases = ['PHASE_1', 'PHASE_2', 'PHASE_3', 'PHASE_4'];
    const currentIndex = phases.indexOf(currentPhase);
    return currentIndex < phases.length - 1 ? phases[currentIndex + 1] : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">Loading RTP data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-rtp-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Return to Play</h1>
          <p className="text-zinc-400 text-sm">Manage injury cases and athlete recovery protocols</p>
        </div>
        {canWrite && (
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="create-rtp-btn">
                <Plus className="w-4 h-4 mr-2" />
                New RTP Case
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">Create RTP Profile</DialogTitle>
                <DialogDescription className="text-zinc-400 text-xs">
                  Set up a new return-to-play case. Injury Type drives season-level analytics — pick &quot;Other&quot; only if none fit.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Athlete</Label>
                    <Select value={newProfile.athlete_id} onValueChange={(v) => {
                      const athlete = athletes.find(a => a.id === v);
                      setNewProfile({...newProfile, athlete_id: v, team_id: athlete?.primary_team_id || ''});
                    }}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue placeholder="Select athlete" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {athletes.map(a => (
                          <SelectItem key={a.id} value={a.id} className="text-white">
                            {a.first_name} {a.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Team</Label>
                    <Select value={newProfile.team_id} onValueChange={(v) => setNewProfile({...newProfile, team_id: v})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue placeholder="Select team" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {teams.map(t => (
                          <SelectItem key={t.id} value={t.id} className="text-white">{t.code}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Injury Type</Label>
                    <Select
                      value={newProfile.injury_type}
                      onValueChange={(val) => setNewProfile({ ...newProfile, injury_type: val })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="create-rtp-injury-type">
                        <SelectValue placeholder="Select injury type" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-700">
                        {INJURY_TYPES.map((it) => (
                          <SelectItem key={it} value={it}>{it}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {newProfile.injury_type === 'Other' && (
                      <p className="text-[10px] text-amber-400" data-testid="rtp-other-hint">
                        Description is required when type is "Other".
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Injury Date</Label>
                    <Input
                      type="date"
                      className="bg-zinc-800 border-zinc-700 text-white"
                      value={newProfile.injury_date}
                      onChange={(e) => setNewProfile({...newProfile, injury_date: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Location</Label>
                    <Input
                      className="bg-zinc-800 border-zinc-700 text-white"
                      placeholder="e.g., Right thigh"
                      value={newProfile.injury_location}
                      onChange={(e) => setNewProfile({...newProfile, injury_location: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Expected Duration (weeks)</Label>
                    <Input
                      type="number"
                      className="bg-zinc-800 border-zinc-700 text-white"
                      placeholder="e.g., 6"
                      value={newProfile.expected_duration_weeks}
                      onChange={(e) => setNewProfile({...newProfile, expected_duration_weeks: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Description</Label>
                  <Textarea
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="Detailed injury description..."
                    value={newProfile.injury_description}
                    onChange={(e) => setNewProfile({...newProfile, injury_description: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Notes</Label>
                  <Textarea
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="Internal staff notes, observations, treatment plan..."
                    rows={3}
                    value={newProfile.notes}
                    onChange={(e) => setNewProfile({...newProfile, notes: e.target.value})}
                    data-testid="create-rtp-notes"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="border-zinc-700 text-zinc-300">
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateProfile} 
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                  disabled={
                    !newProfile.athlete_id ||
                    !newProfile.team_id ||
                    !newProfile.injury_type ||
                    (newProfile.injury_type === 'Other' && !(newProfile.injury_description || '').trim())
                  }
                  data-testid="create-rtp-submit"
                >
                  Create Profile
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Active Cases</p>
                  <p className="text-2xl font-bold text-amber-400">{summary.active_profiles}</p>
                </div>
                <Activity className="w-8 h-8 text-amber-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Active Caps</p>
                  <p className="text-2xl font-bold text-blue-400">{summary.active_caps}</p>
                </div>
                <Target className="w-8 h-8 text-blue-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Recent Clearances</p>
                  <p className="text-2xl font-bold text-green-400">{summary.recent_clearances}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Phase Breakdown</p>
                  <div className="flex gap-2 mt-1">
                    {Object.entries(summary.phase_breakdown || {}).map(([phase, count]) => (
                      count > 0 && (
                        <span key={phase} className="text-xs bg-zinc-800 px-2 py-1 rounded text-zinc-300">
                          P{phase.replace('PHASE_', '')}: {count}
                        </span>
                      )
                    ))}
                  </div>
                </div>
                <TrendingUp className="w-8 h-8 text-zinc-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-800/50">
          <TabsTrigger value="overview" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            Overview
          </TabsTrigger>
          <TabsTrigger value="cases" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            Cases
          </TabsTrigger>
          <TabsTrigger value="caps" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            Load Caps
          </TabsTrigger>
          <TabsTrigger value="weekly" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            Weekly Logs
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-amber-500" />
                Athletes in RTP
              </CardTitle>
            </CardHeader>
            <CardContent>
              {summary?.athletes_in_rtp?.length > 0 ? (
                <div className="space-y-3">
                  {summary.athletes_in_rtp.map((profile) => (
                    <div 
                      key={profile.id} 
                      className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg cursor-pointer hover:bg-zinc-800"
                      onClick={() => openProfileDetail(profile)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-amber-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{profile.athlete_name}</p>
                          <p className="text-zinc-400 text-sm">{profile.injury_type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={PHASE_CONFIG[profile.current_phase]?.color || 'bg-zinc-700'}>
                          {PHASE_CONFIG[profile.current_phase]?.label || profile.current_phase}
                        </Badge>
                        <ArrowRight className="w-4 h-4 text-zinc-500" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No athletes currently in RTP</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cases Tab */}
        <TabsContent value="cases" className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search by athlete or injury..."
                className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="ACTIVE" className="text-white">Active</SelectItem>
                <SelectItem value="CLEARED" className="text-white">Cleared</SelectItem>
                <SelectItem value="CANCELLED" className="text-white">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-4">
            {filteredProfiles.length > 0 ? (
              filteredProfiles.map((profile) => (
                <Card 
                  key={profile.id} 
                  className="bg-zinc-900/50 border-zinc-800 cursor-pointer hover:border-amber-500/30 transition-colors"
                  onClick={() => openProfileDetail(profile)}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                          <User className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{profile.athlete_name}</p>
                          <p className="text-amber-400">{profile.injury_type}</p>
                          <div className="flex items-center gap-2 text-zinc-400 text-sm mt-1">
                            <Calendar className="w-3 h-3" />
                            <span>{profile.injury_date}</span>
                            {profile.injury_location && (
                              <>
                                <span>•</span>
                                <span>{profile.injury_location}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={PHASE_CONFIG[profile.current_phase]?.color || 'bg-zinc-700'}>
                          {PHASE_CONFIG[profile.current_phase]?.label || profile.current_phase}
                        </Badge>
                        <Badge className={STATUS_CONFIG[profile.status]?.color || 'bg-zinc-700'}>
                          {STATUS_CONFIG[profile.status]?.label || profile.status}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-8 text-center text-zinc-500">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No RTP cases found</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Caps Tab — Phase Cap Defaults (Settings) */}
        <TabsContent value="caps" className="space-y-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">RTP Phase Cap Settings</CardTitle>
                  <p className="text-sm text-zinc-500 mt-1">Default load caps per phase. These values define the maximum weekly load for each metric during RTP.</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {['PHASE_1', 'PHASE_2', 'PHASE_3', 'PHASE_4'].map(phase => {
                const phaseCapsForPhase = phaseCaps.filter(c => c.phase === phase);
                if (phaseCapsForPhase.length === 0) return null;
                const phConfig = PHASE_CONFIG[phase] || {};
                return (
                  <div key={phase} className="mb-6 last:mb-0">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className={phConfig.color || 'bg-zinc-700'}>{phConfig.label || phase}</Badge>
                      <span className="text-xs text-zinc-500">{phConfig.description || ''}</span>
                    </div>
                    <div className="grid gap-2">
                      {phaseCapsForPhase.map(cap => (
                        <div key={cap.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-sm border border-zinc-700/50" data-testid={`phase-cap-${cap.id}`}>
                          <div className="flex items-center gap-3 min-w-0 sm:min-w-[140px]">
                            <span className="text-sm font-medium text-zinc-200">{cap.metric.replace(/_/g, ' ')}</span>
                          </div>
                          {editingCap === cap.id ? (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1">
                                <label className="text-[10px] text-zinc-500">Cap:</label>
                                <Input
                                  type="number"
                                  value={editCapValue}
                                  onChange={e => setEditCapValue(e.target.value)}
                                  className="w-20 h-7 text-xs bg-zinc-900 border-zinc-700"
                                  data-testid="edit-cap-value"
                                />
                              </div>
                              <div className="flex items-center gap-1">
                                <label className="text-[10px] text-zinc-500">Max +%:</label>
                                <Input
                                  type="number"
                                  value={editCapIncrease}
                                  onChange={e => setEditCapIncrease(e.target.value)}
                                  className="w-16 h-7 text-xs bg-zinc-900 border-zinc-700"
                                  data-testid="edit-cap-increase"
                                />
                              </div>
                              <Button size="sm" className="h-7 bg-emerald-500 text-black text-xs" onClick={() => handleSaveCap(cap.id)} data-testid="save-cap-btn">Save</Button>
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-zinc-400" onClick={() => setEditingCap(null)}>Cancel</Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <span className="text-amber-400 font-mono text-sm">{cap.cap_value.toLocaleString()}</span>
                                <span className="text-zinc-500 text-xs ml-2">max +{cap.max_increase_pct}%/wk</span>
                              </div>
                              {canWrite && (
                                <button
                                  onClick={() => { setEditingCap(cap.id); setEditCapValue(String(cap.cap_value)); setEditCapIncrease(String(cap.max_increase_pct)); }}
                                  className="text-zinc-500 hover:text-amber-400 transition-colors"
                                  data-testid={`edit-cap-${cap.id}`}
                                >
                                  <Edit className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {phaseCaps.length === 0 && (
                <div className="text-center py-8 text-zinc-500">
                  <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Loading cap settings...</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Athlete Caps */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Active Athlete Caps</CardTitle>
              <p className="text-sm text-zinc-500">Individual athlete load caps currently in effect</p>
            </CardHeader>
            <CardContent>
              {caps.length > 0 ? (
                <div className="space-y-3">
                  {caps.map((cap) => (
                    <div key={cap.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-sm">
                      <div>
                        <p className="text-white font-medium">{cap.athlete_name}</p>
                        <p className="text-zinc-400 text-sm">{cap.metric.replace(/_/g, ' ')}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-amber-400 font-mono">{cap.weekly_cap.toLocaleString()}</p>
                        <p className="text-zinc-500 text-xs">Max +{cap.max_weekly_increase_pct}%/week</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-zinc-500">
                  <p className="text-sm">No individual athlete caps active</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weekly Logs Tab — Enhanced display */}
        <TabsContent value="weekly" className="space-y-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Weekly Load Logs</CardTitle>
                <div className="flex items-center gap-1 text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded">
                  <Info className="w-3 h-3" />
                  <span>Values from GPS/load data aggregated per week. Caps from RTP phase settings.</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {weeklyLogs.length > 0 ? (
                <div className="space-y-4">
                  {weeklyLogs.map((log, idx) => {
                    const prevLog = weeklyLogs[idx + 1]; // sorted desc, so next in array is previous week
                    return (
                      <div key={log.id} className="p-4 bg-zinc-800/50 rounded-sm border border-zinc-700/50" data-testid={`weekly-log-${log.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-zinc-500" />
                            <span className="text-sm font-medium text-white">Week of {log.week_start}</span>
                            {log.athlete_name && <span className="text-xs text-zinc-500">- {log.athlete_name}</span>}
                          </div>
                          <Badge className={
                            log.alert_level === 'RED' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                            log.alert_level === 'AMBER' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                            'bg-green-500/20 text-green-400 border border-green-500/30'
                          } data-testid={`alert-${log.id}`}>
                            {log.alert_level === 'RED' ? 'Over Cap' : log.alert_level === 'AMBER' ? 'Approaching Cap' : 'Within Limits'}
                          </Badge>
                        </div>
                        {log.alert_reasons?.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-1">
                            {log.alert_reasons.map((r, ri) => (
                              <span key={ri} className="text-[11px] bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded">{r}</span>
                            ))}
                          </div>
                        )}
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-zinc-500 border-b border-zinc-700">
                                <th className="text-left py-1.5 pr-4 font-medium">Metric</th>
                                <th className="text-right py-1.5 px-2 font-medium">Actual</th>
                                <th className="text-right py-1.5 px-2 font-medium">Cap</th>
                                <th className="text-right py-1.5 px-2 font-medium">% Used</th>
                                <th className="text-right py-1.5 pl-2 font-medium">vs Prev Wk</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(log.metrics || {}).map(([metric, data]) => {
                                const actual = data.actual ?? data.value ?? 0;
                                const cap = data.cap ?? data.weekly_cap ?? 0;
                                const usagePct = data.usage_pct ?? (cap > 0 ? (actual / cap * 100) : 0);
                                const prevMetric = prevLog?.metrics?.[metric];
                                const prevActual = prevMetric?.actual ?? prevMetric?.value ?? 0;
                                const changePct = prevActual > 0 ? ((actual - prevActual) / prevActual * 100) : null;
                                const isOver = usagePct > 100;
                                const isNear = usagePct > 80 && usagePct <= 100;
                                return (
                                  <tr key={metric} className="border-b border-zinc-800/50">
                                    <td className="py-1.5 pr-4 text-zinc-300 font-medium">{metric.replace(/_/g, ' ')}</td>
                                    <td className="py-1.5 px-2 text-right text-zinc-100 font-mono">{typeof actual === 'number' ? actual.toLocaleString(undefined, {maximumFractionDigits: 0}) : actual}</td>
                                    <td className="py-1.5 px-2 text-right text-zinc-500 font-mono">{typeof cap === 'number' ? cap.toLocaleString(undefined, {maximumFractionDigits: 0}) : cap}</td>
                                    <td className={`py-1.5 px-2 text-right font-mono font-semibold ${isOver ? 'text-red-400' : isNear ? 'text-amber-400' : 'text-emerald-400'}`}>
                                      {usagePct.toFixed(0)}%
                                    </td>
                                    <td className="py-1.5 pl-2 text-right font-mono">
                                      {changePct !== null ? (
                                        <span className={changePct > 0 ? 'text-amber-400' : changePct < 0 ? 'text-blue-400' : 'text-zinc-500'}>
                                          {changePct > 0 ? '+' : ''}{changePct.toFixed(0)}%
                                        </span>
                                      ) : (
                                        <span className="text-zinc-600">-</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No weekly logs yet</p>
                  <p className="text-xs mt-1">Weekly logs are generated automatically from GPS data for athletes in RTP.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Profile Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0">
            <div className="flex items-center justify-between gap-3 pr-8">
              <DialogTitle className="text-white">RTP Case Details</DialogTitle>
              {selectedProfile && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleExportRtpReport(selectedProfile)}
                  disabled={exportingRtp}
                  className="border-cyan-500/40 text-cyan-100 bg-cyan-950/30 hover:bg-cyan-900/40"
                  data-testid="export-rtp-report-btn"
                >
                  {exportingRtp ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileDown className="w-4 h-4 mr-2" />
                  )}
                  Export RTP Report
                </Button>
              )}
            </div>
          </DialogHeader>
          {selectedProfile && (
            <div className="flex flex-col min-h-0 flex-1">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 pb-4 shrink-0">
                <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                  <User className="w-7 h-7 text-amber-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-white text-lg font-medium">{selectedProfile.athlete_name}</p>
                  <p className="text-amber-400">{selectedProfile.injury_type}</p>
                </div>
                <div className="sm:ml-auto flex items-center gap-2 flex-wrap">
                  <Badge className={PHASE_CONFIG[selectedProfile.current_phase]?.color || 'bg-zinc-700'}>
                    {PHASE_CONFIG[selectedProfile.current_phase]?.label || selectedProfile.current_phase}
                  </Badge>
                  <Badge className={STATUS_CONFIG[selectedProfile.status]?.color || 'bg-zinc-700'}>
                    {STATUS_CONFIG[selectedProfile.status]?.label || selectedProfile.status}
                  </Badge>
                </div>
              </div>

              {/* Tabs */}
              <Tabs value={detailTab} onValueChange={setDetailTab} className="flex flex-col min-h-0 flex-1">
                <TabsList className="grid w-full grid-cols-3 bg-zinc-800/50 shrink-0" data-testid="rtp-detail-tabs">
                  <TabsTrigger value="overview" className="data-[state=active]:bg-zinc-700 text-zinc-300">Overview</TabsTrigger>
                  <TabsTrigger value="sessions" className="data-[state=active]:bg-zinc-700 text-zinc-300">Session History</TabsTrigger>
                  <TabsTrigger value="notes" className="data-[state=active]:bg-zinc-700 text-zinc-300">Notes</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="flex-1 overflow-y-auto mt-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-zinc-500 text-sm">Injury Date</p>
                      <p className="text-white">{selectedProfile.injury_date}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Location</p>
                      <p className="text-white">{selectedProfile.injury_location || '-'}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Expected Duration</p>
                      <p className="text-white">{selectedProfile.expected_duration_weeks ? `${selectedProfile.expected_duration_weeks} weeks` : '-'}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Days in RTP</p>
                      <p className="text-white">{selectedProfile.injury_date ? Math.floor((Date.now() - new Date(selectedProfile.injury_date).getTime()) / 86400000) : '-'}</p>
                    </div>
                  </div>
                  
                  {selectedProfile.injury_description && (
                    <div className="pt-4 border-t border-zinc-800">
                      <p className="text-zinc-500 text-sm mb-1">Description</p>
                      <p className="text-zinc-300 text-sm">{selectedProfile.injury_description}</p>
                    </div>
                  )}
                  
                  {canWrite && selectedProfile.status === 'ACTIVE' && (
                    <div className="pt-4 border-t border-zinc-800 space-y-3">
                      <div>
                        <p className="text-zinc-500 text-sm mb-2">Change Phase</p>
                        <div className="flex flex-wrap gap-2">
                          {['PHASE_1', 'PHASE_2', 'PHASE_3', 'PHASE_4'].map(phase => {
                            const isCurrent = selectedProfile.current_phase === phase;
                            const currentIdx = ['PHASE_1', 'PHASE_2', 'PHASE_3', 'PHASE_4'].indexOf(selectedProfile.current_phase);
                            const targetIdx = ['PHASE_1', 'PHASE_2', 'PHASE_3', 'PHASE_4'].indexOf(phase);
                            const isRegress = targetIdx < currentIdx;
                            return (
                              <Button 
                                key={phase}
                                size="sm"
                                disabled={isCurrent}
                                variant={isCurrent ? "default" : "outline"}
                                className={isCurrent
                                  ? `${PHASE_CONFIG[phase]?.color || 'bg-zinc-700'} text-white cursor-default`
                                  : isRegress
                                    ? 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10'
                                    : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'
                                }
                                onClick={() => {
                                  if (isRegress) {
                                    setPhaseChangeTarget({ profileId: selectedProfile.id, phase, isRegress: true });
                                  } else {
                                    handleChangePhase(selectedProfile.id, phase);
                                  }
                                }}
                                data-testid={`phase-btn-${phase}`}
                              >
                                {isRegress && <ArrowLeft className="w-3 h-3 mr-1" />}
                                {!isRegress && !isCurrent && <ArrowRight className="w-3 h-3 mr-1" />}
                                {PHASE_CONFIG[phase]?.label || phase}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                      <Button 
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleClearProfile(selectedProfile.id)}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Clear Athlete
                      </Button>
                    </div>
                  )}
                </TabsContent>

                {/* Session History Tab */}
                <TabsContent value="sessions" className="flex-1 overflow-y-auto mt-4" data-testid="rtp-session-history">
                  {sessionsLoading ? (
                    <div className="text-center py-8 text-zinc-500">Loading sessions...</div>
                  ) : profileSessions.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500">
                      <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No RTP sessions found for this case</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {profileSessions.map(session => {
                        const statusColors = {
                          COMPLETED: 'bg-green-500/10 border-green-500/30 text-green-400',
                          IN_PROGRESS: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
                          MISSED: 'bg-red-500/10 border-red-500/30 text-red-400',
                          NOT_ASSIGNED: 'bg-zinc-800 border-zinc-700 text-zinc-400',
                          ASSIGNED: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',
                          PARTIAL: 'bg-orange-500/10 border-orange-500/30 text-orange-400',
                        };
                        const sColor = statusColors[session.status] || statusColors.NOT_ASSIGNED;
                        const blocks = session.content_json?.blocks || [];
                        return (
                          <div key={session.id} className="border border-zinc-800 rounded-lg p-3 bg-zinc-800/30" data-testid={`rtp-session-${session.id}`}>
                            <div className="flex items-center justify-between mb-1">
                              <div>
                                <span className="text-zinc-100 text-sm font-medium">{session.title}</span>
                                <span className="text-zinc-500 text-xs ml-2">{session.date}</span>
                              </div>
                              <Badge variant="outline" className={`text-[10px] ${sColor}`}>
                                {session.status.replace('_', ' ')}
                              </Badge>
                            </div>
                            {session.description && (
                              <p className="text-zinc-400 text-xs mb-1 whitespace-pre-wrap break-words">{session.description}</p>
                            )}
                            <div className="flex items-center gap-3 text-xs text-zinc-500">
                              {session.duration_planned_min && <span>{session.duration_planned_min} min</span>}
                              {session.location && <span>{session.location}</span>}
                              {session.rpe != null && <span className="text-amber-400">RPE: {session.rpe}</span>}
                              {session.type && <Badge variant="outline" className="text-[9px] border-zinc-700">{session.type}</Badge>}
                            </div>
                            {blocks.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-zinc-800">
                                <p className="text-zinc-500 text-[10px] uppercase tracking-wide mb-1">Prescribed Content</p>
                                <div className="space-y-1">
                                  {blocks.map((b, i) => (
                                    <div key={i} className="text-xs text-zinc-400">
                                      <div className="flex items-center gap-1">
                                        <div className="w-1.5 h-1.5 rounded-full bg-amber-500/60" />
                                        <span className="text-zinc-200">{b.name || b.type}</span>
                                        {b.duration_min && <span className="text-zinc-500 ml-1">({b.duration_min} min)</span>}
                                      </div>
                                      {b.exercises && b.exercises.length > 0 && (
                                        <div className="pl-4 space-y-0.5 mt-0.5">
                                          {b.exercises.map((ex, j) => (
                                            <div key={j} className="text-[11px] text-zinc-500">
                                              - {ex.name}{ex.sets && ex.reps ? ` (${ex.sets}x${ex.reps})` : ''}{ex.duration_sec ? ` (${Math.round(ex.duration_sec/60)} min)` : ''}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {session.content_json?.notes && (
                                  <p className="text-[10px] text-amber-400/60 mt-1 italic">{session.content_json.notes}</p>
                                )}
                              </div>
                            )}
                            {session.athlete_notes && (
                              <p className="text-xs text-cyan-400/80 mt-1 italic">Athlete note: {session.athlete_notes}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>

                {/* Notes Tab */}
                <TabsContent value="notes" className="flex-1 overflow-y-auto mt-4" data-testid="rtp-notes-tab">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-zinc-400 text-sm font-medium">Case Notes</p>
                      {canWrite && !editingNotes && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-zinc-700 text-zinc-300 h-7 text-xs"
                          onClick={() => { setEditingNotes(true); setNotesValue(selectedProfile.notes || ''); }}
                          data-testid="edit-notes-btn"
                        >
                          <Edit3 className="w-3 h-3 mr-1" /> Edit
                        </Button>
                      )}
                    </div>
                    {editingNotes ? (
                      <div className="space-y-2">
                        <Textarea
                          className="bg-zinc-800 border-zinc-700 text-white min-h-[180px]"
                          placeholder="Add notes about this RTP case, treatment plan, observations..."
                          value={notesValue}
                          onChange={(e) => setNotesValue(e.target.value)}
                          data-testid="rtp-notes-textarea"
                        />
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => setEditingNotes(false)}>
                            Cancel
                          </Button>
                          <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black" onClick={handleSaveNotes} data-testid="save-notes-btn">
                            Save Notes
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 bg-zinc-800/50 rounded-lg min-h-[100px]">
                        {selectedProfile.notes ? (
                          <p className="text-zinc-300 text-sm whitespace-pre-wrap">{selectedProfile.notes}</p>
                        ) : (
                          <p className="text-zinc-600 text-sm italic">No notes yet. Click Edit to add notes.</p>
                        )}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase Regression Confirmation Dialog */}
      <Dialog open={!!phaseChangeTarget} onOpenChange={(open) => { if (!open) setPhaseChangeTarget(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm" data-testid="phase-regress-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Confirm Phase Regression
            </DialogTitle>
          </DialogHeader>
          {phaseChangeTarget && selectedProfile && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-zinc-300">
                Move <span className="font-semibold text-white">{selectedProfile.athlete_name}</span> from{' '}
                <Badge className={PHASE_CONFIG[selectedProfile.current_phase]?.color || 'bg-zinc-700'}>
                  {PHASE_CONFIG[selectedProfile.current_phase]?.label}
                </Badge>
                {' '}back to{' '}
                <Badge className={PHASE_CONFIG[phaseChangeTarget.phase]?.color || 'bg-zinc-700'}>
                  {PHASE_CONFIG[phaseChangeTarget.phase]?.label}
                </Badge>?
              </p>
              <p className="text-xs text-zinc-500">
                Weekly logs will remain intact. Load caps will update according to the selected phase.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => setPhaseChangeTarget(null)} data-testid="cancel-regress">
              Cancel
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-black"
              onClick={() => {
                if (phaseChangeTarget) handleChangePhase(phaseChangeTarget.profileId, phaseChangeTarget.phase);
              }}
              data-testid="confirm-regress"
            >
              Confirm Regression
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
